import { CoreButton } from '@ingenico/react-ui/core-button';
import { CoreDialog } from '@ingenico/react-ui/core-dialog';
import { CoreIconButton } from '@ingenico/react-ui/core-icon-button';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import { DialogActions, DialogContent, DialogTitle, Divider } from '@mui/material';
import type { ReactElement } from 'react';
import { Fragment } from 'react';
import styles from './ParameterModuleContentParametersModal.module.scss';
import { useParameterModuleContentParametersModal } from './useParameterModuleContentParametersModal.hook';

export interface ParameterModuleContentParametersModalSection {
  readonly title: string;
  readonly id: string;
  readonly count: number;
}

export interface ParameterModuleContentParametersModalProps {
  readonly isOpen: boolean;
  readonly onClose?: () => void;
  readonly onConfirm?: () => Promise<void>;
  readonly onCancelParameter?: (shouldResetAll: boolean, parameterId?: string) => void;
  readonly mode?: 'confirm' | 'view';
  readonly sections: ParameterModuleContentParametersModalSection[];
  readonly onError?: (error: Error) => void;
}

export function ParameterModuleContentParametersModal({
  isOpen,
  onClose,
  onConfirm,
  mode,
  onCancelParameter,
  sections,
  onError,
}: ParameterModuleContentParametersModalProps): ReactElement {
  const {
    title,
    subtitle,
    cancelButtonLabel,
    confirmButtonLabel,
    isLoading,
    handleConfirm,
    shouldHideConfirmButton,
    cancelParameterLabel,
    changesLabel,
    hasErrors,
    handleClose,
  } = useParameterModuleContentParametersModal({ onConfirm, mode, onClose, onError });

  return (
    <CoreDialog
      onClose={handleClose}
      classes={{ paper: styles.paper, root: styles.basicDialogModal }}
      fullWidth
      maxWidth={'sm'}
      open={isOpen}
      slotProps={{ transition: { onExited: handleClose } }}
    >
      <DialogTitle className={styles.header}>
        <div className={styles.dialogTitle}>
          <CoreTypography variant={CoreTypographyVariant.Title01Medium}>{title}</CoreTypography>
          <CoreTypography variant={CoreTypographyVariant.Body02Regular}>{subtitle}</CoreTypography>
        </div>
        <CoreIconButton onClick={handleClose}>
          <CloseIcon />
        </CoreIconButton>
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <div className={styles.contentContainer}>
          {sections.map((section, index) =>
            section.count > 0 ? (
              <Fragment key={section.id}>
                <div className={styles.section}>
                  <div className={styles.contentContainer}>
                    <CoreTypography variant={CoreTypographyVariant.Title02Medium}>{section.title}</CoreTypography>
                    <div className={styles.labelsContainer}>
                      <CoreTypography variant={CoreTypographyVariant.Body02Regular}>{changesLabel}</CoreTypography>
                      <div className={styles.counterContainer}>
                        <CoreTypography
                          variant={CoreTypographyVariant.Body02Medium}
                        >{`${section.count}`}</CoreTypography>
                      </div>
                    </div>
                  </div>
                  <CoreButton variant="text" onClick={() => onCancelParameter?.(false, section.id)}>
                    {cancelParameterLabel}
                  </CoreButton>
                </div>
                {index < sections.length - 1 ? <Divider /> : undefined}
              </Fragment>
            ) : undefined,
          )}
        </div>
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <div className={styles.action}>
          <CoreButton
            size="small"
            onClick={handleClose}
            variant={mode === 'confirm' ? 'text' : 'outlined'}
            disabled={isLoading}
          >
            {cancelButtonLabel}
          </CoreButton>
          {!shouldHideConfirmButton && (
            <CoreButton
              startIcon={hasErrors ? <ReplayIcon /> : undefined}
              onClick={handleConfirm}
              loading={isLoading}
              variant="contained"
              size="small"
            >
              {confirmButtonLabel}
            </CoreButton>
          )}
        </div>
      </DialogActions>
    </CoreDialog>
  );
}
